import { AlertTriangle, Clock, MapPin, Phone, User, Activity, Hospital, FileText } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    incidentId: string;
    emergencyType: string;
    severity: string;
    callerName?: string;
    callerPhone?: string;
    locationAddress: string;
    dispatchTime: string;
    responseTime: string;
    hospitalName: string;
    triageCategory: string;
    initialAssessment: string;
    interventions: string[];
    outcomeStatus: string;
    handoverDoctor?: string;
    handoverTime?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

function getSeverityStyle(severity: string) {
  const s = (severity || '').toUpperCase();
  if (s === 'CRITICAL' || s === 'LIFE-THREATENING') return 'bg-red-100 text-red-700 border-red-300';
  if (s === 'SEVERE' || s === 'HIGH') return 'bg-orange-100 text-orange-700 border-orange-300';
  if (s === 'MODERATE') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  return 'bg-green-100 text-green-700 border-green-300';
}

function getTriageStyle(category: string) {
  const c = (category || '').toUpperCase();
  if (c === 'RED' || c === 'IMMEDIATE') return 'bg-red-100 text-red-700';
  if (c === 'YELLOW' || c === 'DELAYED') return 'bg-yellow-100 text-yellow-700';
  if (c === 'GREEN' || c === 'MINOR') return 'bg-green-100 text-green-700';
  if (c === 'BLACK' || c === 'EXPECTANT') return 'bg-gray-800 text-white';
  return 'bg-gray-100 text-gray-700';
}

export default function ECCEmergencyCertificateTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            Emergency Certificate
          </span>
        </div>
        <span
          className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${getSeverityStyle(data.severity)}`}
        >
          {data.severity}
        </span>
      </div>

      {/* Incident Details Grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Incident ID:</span>
          <span className="text-gray-800 font-mono">{data.incidentId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Emergency Type:</span>
          <span className="text-gray-800">{data.emergencyType}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Dispatch:</span>
          <span className="text-gray-800">{data.dispatchTime}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-3 h-3 text-gray-500" />
          <span className="font-semibold text-gray-600">Response:</span>
          <span className="text-gray-800">{data.responseTime}</span>
        </div>
        <div className="col-span-2 flex items-start gap-2">
          <MapPin className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />
          <span className="font-semibold text-gray-600">Location:</span>
          <span className="text-gray-800">{data.locationAddress}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Triage Category:</span>
          <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${getTriageStyle(data.triageCategory)}`}>
            {data.triageCategory}
          </span>
        </div>
      </div>

      {/* Caller Information */}
      {(data.callerName || data.callerPhone) && (
        <div className="bg-blue-50 px-3 py-2 border border-blue-200 rounded text-[10px]">
          <div className="flex items-center gap-1.5 mb-1">
            <Phone className="w-3 h-3 text-blue-600" />
            <span className="font-bold text-blue-700 uppercase tracking-wider text-[9px]">
              Caller Information
            </span>
          </div>
          <div className="flex gap-3">
            {data.callerName && (
              <div className="flex gap-2">
                <User className="w-3 h-3 text-gray-500" />
                <span className="font-semibold text-gray-600">Name:</span>
                <span className="text-gray-800">{data.callerName}</span>
              </div>
            )}
            {data.callerPhone && (
              <div className="flex gap-2">
                <Phone className="w-3 h-3 text-gray-500" />
                <span className="font-semibold text-gray-600">Phone:</span>
                <span className="text-gray-800 font-mono">{data.callerPhone}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Initial Assessment */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <Activity className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Initial Assessment
          </h3>
        </div>
        <p className="text-[10px] text-gray-700 leading-relaxed bg-gray-50 px-3 py-2 border border-gray-200 rounded">
          {data.initialAssessment}
        </p>
      </div>

      {/* Interventions */}
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <FileText className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Interventions Performed
          </h3>
        </div>
        <ol className="list-decimal list-inside space-y-1 text-[10px] text-gray-700 bg-gray-50 px-3 py-2 border border-gray-200 rounded">
          {data.interventions.map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ol>
      </div>

      {/* Hospital Handover */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 bg-gray-50 px-3 py-2.5 border border-gray-200 rounded text-[10px]">
        <div className="col-span-2 flex items-center gap-1.5 mb-1">
          <Hospital className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Hospital Handover
          </h3>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Hospital:</span>
          <span className="text-gray-800">{data.hospitalName}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Outcome:</span>
          <span className="text-gray-800 font-medium">{data.outcomeStatus}</span>
        </div>
        {data.handoverDoctor && (
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">Handover Doctor:</span>
            <span className="text-gray-800">{data.handoverDoctor}</span>
          </div>
        )}
        {data.handoverTime && (
          <div className="flex gap-2">
            <span className="font-semibold text-gray-600">Handover Time:</span>
            <span className="text-gray-800">{data.handoverTime}</span>
          </div>
        )}
      </div>

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
